/**
 * Newsletter Popup Component
 *
 * Manages the display of a newsletter signup popup that appears once per user.
 * Uses localStorage to track if the user has already seen the popup.
 */

class NewsletterPopup {
  constructor() {
    this.STORAGE_KEY = 'newsletter_popup_seen';
    this.popup = null;
    this.hasBeenSeen = false;
    this.delayMs = 3000; // Default delay

    this.init();
  }

  /**
   * Initialize the popup functionality
   */
  init() {
    // Wait for DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  /**
   * Setup the popup after DOM is ready
   */
  setup() {
    this.popup = document.getElementById('newsletter-popup');

    if (!this.popup) {
      console.warn('Newsletter popup element not found');
      return;
    }

    // Get delay from data attribute (in seconds) and convert to milliseconds
    const delaySeconds = parseInt(this.popup.getAttribute('data-delay') || '3', 10);
    this.delayMs = delaySeconds * 1000;

    // Check if user has already seen the popup
    this.hasBeenSeen = this.checkIfSeen();

    if (!this.hasBeenSeen) {
      // Show popup after delay
      setTimeout(() => {
        this.show();
      }, this.delayMs);
    }

    // Listen for popup close events
    this.attachCloseListeners();

    // Listen for successful form submission
    this.attachFormListener();
  }

  /**
   * Check if user has already seen the popup
   * @returns {boolean}
   */
  checkIfSeen() {
    try {
      const seen = localStorage.getItem(this.STORAGE_KEY);
      return seen === 'true';
    } catch (e) {
      console.error('Error reading from localStorage:', e);
      return false;
    }
  }

  /**
   * Mark popup as seen in localStorage
   */
  markAsSeen() {
    try {
      localStorage.setItem(this.STORAGE_KEY, 'true');
      this.hasBeenSeen = true;
    } catch (e) {
      console.error('Error writing to localStorage:', e);
    }
  }

  /**
   * Show the popup
   */
  show() {
    if (!this.popup || this.hasBeenSeen) return;

    const dialog = this.popup.querySelector('dialog');
    if (dialog) {
      dialog.showModal();
    }
  }

  /**
   * Close the popup
   */
  close() {
    if (!this.popup) return;

    const dialog = this.popup.querySelector('dialog');
    if (dialog && dialog.open) {
      dialog.close();
    }

    this.markAsSeen();
  }

  /**
   * Attach event listeners for closing the popup
   */
  attachCloseListeners() {
    if (!this.popup) return;

    const dialog = this.popup.querySelector('dialog');
    const closeButton = this.popup.querySelector('.newsletter-popup__close-button');

    // Mark as seen when dialog is closed
    if (dialog) {
      dialog.addEventListener('close', () => {
        this.markAsSeen();
      });
    }

    // Handle close button click
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.close();
      });
    }
  }

  /**
   * Attach listener for successful form submission
   */
  attachFormListener() {
    if (!this.popup) return;

    const form = this.popup.querySelector('form');
    if (!form) return;

    // Check if form was successfully submitted (success message is present)
    const successMessage = this.popup.querySelector('.email-signup__message--success');
    if (successMessage) {
      // Mark as seen and close after a delay to let user see success message
      setTimeout(() => {
        this.close();
      }, 2000);
    }

    // Listen for form submission
    form.addEventListener('submit', () => {
      // Mark as seen on submit to prevent showing again even if submission fails
      // User has interacted with the popup, so we don't want to show it again
      setTimeout(() => {
        this.markAsSeen();
      }, 100);
    });
  }
}

// Initialize the popup
if (!window.newsletterPopupInstance) {
  window.newsletterPopupInstance = new NewsletterPopup();
}
